import React, { useState } from 'react';
import { Product } from '../types';

interface ProductFormProps {
  productId?: string;
  initialData?: Partial<Product>;
  onSubmit: (product: Omit<Product, 'dateAdded' | 'lastUpdated'>) => void;
  onCancel: () => void;
  expiryEditable?: boolean; // If false, expiry date comes from the code (DataMatrix) and the field is hidden
  existingCategories?: string[]; // List of known categories (for suggestions)
}

const ProductForm: React.FC<ProductFormProps> = ({ 
  productId, 
  initialData = {}, 
  onSubmit, 
  onCancel,
  expiryEditable = true,
  existingCategories = []
}) => {
  const [name, setName] = useState(initialData.name || '');
  const [quantity, setQuantity] = useState(initialData.quantity || 1);
  const [expiryDate, setExpiryDate] = useState<string | undefined>(initialData.expiryDate);
  const [category, setCategory] = useState<string>(initialData.category || '');
  const [errors, setErrors] = useState<{ name?: string; quantity?: string; expiryDate?: string }>({});

  const validateForm = (): boolean => {
    const newErrors: { name?: string; quantity?: string; expiryDate?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Product name is required';
    }
    
    if (quantity <= 0) {
      newErrors.quantity = 'Quantity must be greater than 0';
    }

    if (expiryEditable) {
      if (!expiryDate || !/^\d{4}-\d{2}-\d{2}$/.test(expiryDate)) {
        newErrors.expiryDate = 'Provide expiry date (YYYY-MM-DD)';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      onSubmit({
        id: productId || '',
        name: name.trim(),
        quantity,
        expiryDate,
        baseCode: initialData.baseCode,
        barcodeFormat: initialData.barcodeFormat,
        category: category.trim() || undefined
      });
    }
  };

  const incrementQuantity = () => {
    setQuantity(prev => prev + 1);
  };

  const decrementQuantity = () => {
    setQuantity(prev => Math.max(1, prev - 1));
  };

  return (
    <div className="product-form">
      <h2>{productId ? 'Edit product' : 'Add new product'}</h2>
      
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="product-name">Product name</label>
          <input
            type="text"
            id="product-name"
            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          {errors.name && <div className="invalid-feedback">{errors.name}</div>}
        </div>
        
        {!expiryEditable ? (
          // DataMatrix mode – expiry date comes from the code, show as read-only
          initialData?.expiryDate ? (
            <div className="form-group">
              <label>Expiry date</label>
              <div className="readonly-field">{initialData.expiryDate}</div>
            </div>
          ) : null
        ) : (
          <div className="form-group">
            <label htmlFor="product-expiry">Expiry date</label>
            <input
              type="date"
              id="product-expiry"
              className={`form-control ${errors.expiryDate ? 'is-invalid' : ''}`}
              value={expiryDate || ''}
              onChange={(e) => setExpiryDate(e.target.value || undefined)}
            />
            {errors.expiryDate && <div className="invalid-feedback">{errors.expiryDate}</div>}
          </div>
        )}
        
        <div className="form-group">
          <label htmlFor="product-category">Category</label>
          <input
            type="text"
            id="product-category"
            className="form-control"
            list="categories-datalist"
            placeholder="Select or type a new category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          />
          <datalist id="categories-datalist">
            {existingCategories.map((cat) => (
              <option key={cat} value={cat} />
            ))}
          </datalist>
        </div>
        
        <div className="form-group">
          <label htmlFor="product-quantity">Quantity</label>
          <div className="quantity-control">
            <button 
              type="button"
              className="quantity-btn" 
              onClick={decrementQuantity}
              disabled={quantity <= 1}
            >
              -
            </button>
            <input
              type="number"
              id="product-quantity"
              className={`form-control ${errors.quantity ? 'is-invalid' : ''}`}
              value={quantity}
              min="1"
              onChange={(e) => setQuantity(parseInt(e.target.value) || 0)}
            />
            <button 
              type="button"
              className="quantity-btn" 
              onClick={incrementQuantity}
            >
              +
            </button>
          </div>
          {errors.quantity && <div className="invalid-feedback">{errors.quantity}</div>}
        </div>
        
        <div className="form-buttons">
          <button type="submit" className="btn btn-primary">
            {productId ? 'Save changes' : 'Add product'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm; 